import React, { useEffect, useState } from "react";
import { TextInput, Button, Stack, Card, Title, Group, Textarea, ActionIcon, Divider, Text, MultiSelect, Loader, Alert, Select } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useNavigate } from "react-router-dom";
import { useCreateMeeting } from "../hooks";
import { IconPlus, IconTrash, IconAlertCircle } from "@tabler/icons-react";
import { userApi } from "../../../api/users";
import { bidCommitteeApi } from "../../../api/bidCommittee";
import { AgendaTenderDocumentField, type AgendaTenderDocumentValue } from "../components/AgendaTenderDocumentField";

interface AgendaItem {
  subject: string;
  notes: string;
  tenderId: string | null;
  singleSourceId: string | null;
  projectSummarySheetId: string | null;
  tenderDocument: AgendaTenderDocumentValue | null;
}

interface MeetingFormValues {
  meetingNumber: string;
  title: string;
  location: string;
  plannedStart: Date;
  agendaItems: AgendaItem[];
  memberIds: string[];
}

export const MeetingForm: React.FC = () => {
  const navigate = useNavigate();
  const createMeeting = useCreateMeeting();
  const [bidCommitteeMembers, setBidCommitteeMembers] = useState<Array<{ value: string; label: string }>>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [eligibleTenders, setEligibleTenders] = useState<Array<{ value: string; label: string }>>([]);
  const [eligibleSingleSourceForms, setEligibleSingleSourceForms] = useState<Array<{ value: string; label: string }>>([]);
  const [loadingTenders, setLoadingTenders] = useState(false);
  const [loadingSingleSource, setLoadingSingleSource] = useState(false);
  const [eligiblePss, setEligiblePss] = useState<Array<{ value: string; label: string }>>([]);
  const [loadingPss, setLoadingPss] = useState(false);
  const [uploadingDocuments, setUploadingDocuments] = useState(0);

  const form = useForm<MeetingFormValues>({
    initialValues: {
      meetingNumber: "",
      title: "",
      location: "",
      plannedStart: new Date(),
      agendaItems: [{ subject: '', notes: '', tenderId: null, singleSourceId: null, projectSummarySheetId: null, tenderDocument: null }],
      memberIds: [],
    },
    validate: {
      meetingNumber: (value) => (!value.trim() ? "Meeting number is required" : null),
      title: (value) => (!value ? "Title is required" : null),
      location: (value) => (!value ? "Location is required" : null),
    },
  });

  // Load suggested meeting number, bid committee members, tenders, and single source forms on mount
  useEffect(() => {
    loadSuggestedMeetingNumber();
    loadBidCommitteeMembers();
    loadEligibleTenders();
    loadEligibleSingleSourceForms();
    loadEligibleProjectSummarySheets();
  }, []);

  const loadSuggestedMeetingNumber = async () => {
    try {
      const response = await bidCommitteeApi.getNextMeetingNumber();
      if (!form.getValues().meetingNumber) {
        form.setFieldValue("meetingNumber", response.data.meetingNumber);
      }
    } catch (error) {
      // Non-blocking; user can still enter a number manually
      console.error('Error loading suggested meeting number:', error);
    }
  };

  const loadBidCommitteeMembers = async () => {
    setLoadingMembers(true);
    try {
      const users = await userApi.getUsersForEvaluationReport();
      const members = users
        .filter((u) => u.role === 'BID_COMMITTEE_MEMBER')
        .map((u) => ({ value: u.id, label: u.fullName }));
      setBidCommitteeMembers(members);
    } catch (error) {
      console.error('Error loading bid committee members:', error);
    } finally {
      setLoadingMembers(false);
    }
  };

  const loadEligibleTenders = async () => {
    setLoadingTenders(true);
    try {
      const response = await bidCommitteeApi.getEligibleTenders();
      console.log('Eligible tenders response:', response.data);
      const tenders = response.data.map((t) => ({
        value: t.id,
        label: `${t.referenceNo} - ${t.description}`,
      }));
      console.log('Mapped tenders:', tenders);
      setEligibleTenders(tenders);
    } catch (error) {
      console.error('Error loading eligible tenders:', error);
    } finally {
      setLoadingTenders(false);
    }
  };

  const loadEligibleProjectSummarySheets = async () => {
    setLoadingPss(true);
    try {
      const response = await bidCommitteeApi.getEligibleProjectSummarySheets();
      setEligiblePss(
        response.data.map((s) => ({
          value: s.id,
          label: `${s.summarySheetNumber} — ${s.projectName}`,
        }))
      );
    } catch (error) {
      console.error("Error loading eligible project summary sheets:", error);
    } finally {
      setLoadingPss(false);
    }
  };

  const loadEligibleSingleSourceForms = async () => {
    setLoadingSingleSource(true);
    try {
      const response = await bidCommitteeApi.getEligibleSingleSourceForms();
      console.log('Eligible single source forms response:', response.data);
      const forms = response.data.map((f) => ({
        value: f.id,
        label: `${f.formNumber} - ${f.projectName ?? f.title} (${f.vendorName})`,
      }));
      console.log('Mapped single source forms:', forms);
      setEligibleSingleSourceForms(forms);
    } catch (error) {
      console.error('Error loading eligible single source forms:', error);
    } finally {
      setLoadingSingleSource(false);
    }
  };

  const addAgendaItem = () => {
    form.insertListItem('agendaItems', { subject: '', notes: '', tenderId: null, singleSourceId: null, projectSummarySheetId: null, tenderDocument: null });
  };

  const removeAgendaItem = (index: number) => {
    form.removeListItem('agendaItems', index);
  };

  const handleSubmit = (values: MeetingFormValues) => {
    console.log("Submitting meeting form:", values);

    createMeeting.mutate(
      {
        meetingNumber: values.meetingNumber.trim(),
        title: values.title,
        location: values.location,
        plannedStart: values.plannedStart.toISOString(),
        agendaItems: values.agendaItems.map((item, index) => ({
          orderNo: index + 1,
          subject: item.subject,
          notes: item.notes || null,
          tenderId: item.tenderId || null,
          singleSourceId: item.singleSourceId || null,
          projectSummarySheetId: item.projectSummarySheetId || null,
          tenderDocumentId: item.tenderDocument?.id || null,
        })),
        memberIds: values.memberIds,
      },
      {
        onSuccess: (data) => {
          console.log("Meeting created successfully:", data);
          navigate("/bid-committee/meetings");
        },
        onError: (error: any) => {
          console.error("Failed to create meeting:", error);
          if (error.response?.status === 409) {
            form.setFieldError("meetingNumber", error.response.data?.error);
          }
        },
      }
    );
  };

  return (
    <Stack gap="lg">
      <Title order={2}>Create New Meeting</Title>

      <Card>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <TextInput
              label="Meeting Number"
              placeholder="e.g., BC-2026/001"
              description="Must be unique. A suggested number is pre-filled; change it if needed."
              required
              {...form.getInputProps("meetingNumber")}
            />

            <TextInput
              label="Title"
              placeholder="e.g., Bid Committee #1/23"
              required
              {...form.getInputProps("title")}
            />

            <Group grow>
              <TextInput
                label="Location"
                placeholder="e.g., Board Room"
                required
                {...form.getInputProps("location")}
              />
              <DatePickerInput
                label="Planned Start"
                placeholder="Select date and time"
                required
                {...form.getInputProps("plannedStart")}
              />
            </Group>

            <Divider label="Bid Committee Members" labelPosition="center" my="md" />

            {loadingMembers ? (
              <Loader size="md" />
            ) : (
              <>
                {bidCommitteeMembers.length === 0 && (
                  <Alert icon={<IconAlertCircle size={16} />} color="yellow">
                    No bid committee members found. Please add users with BID_COMMITTEE_MEMBER role.
                  </Alert>
                )}
                <MultiSelect
                  label="Bid Committee Members"
                  placeholder="Select bid committee members"
                  data={bidCommitteeMembers}
                  value={form.values.memberIds}
                  onChange={(value) => form.setFieldValue('memberIds', value)}
                  searchable
                  clearable
                />
              </>
            )}

            <Divider label="Agenda Items" labelPosition="center" my="md" />

            <Stack gap="md">
              {form.values.agendaItems.map((item, index) => (
                <Card key={index} shadow="xs" p="md" withBorder>
                  <Group justify="space-between" mb="md">
                    <Text fw={500}>Agenda Item {index + 1}</Text>
                    <ActionIcon
                      color="red"
                      variant="light"
                      disabled={uploadingDocuments > 0}
                      onClick={() => removeAgendaItem(index)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                  <Stack gap="md">
                    <Textarea
                      label="Agenda Subject"
                      description=""
                      placeholder="Write agenda subject"
                      required
                      minRows={3}
                      value={item.subject}
                      onChange={(e) => {
                        const updatedItems = [...form.values.agendaItems];
                        updatedItems[index] = { ...updatedItems[index], subject: e.currentTarget.value };
                        form.setFieldValue('agendaItems', updatedItems);
                      }}
                      error={form.errors[`agendaItems.${index}.subject` as keyof typeof form.errors]}
                    />
                    <Textarea
                      label="Presented Information / Notes"
                      description=""
                      placeholder="Write notes or presented information "
                      minRows={6}
                      value={item.notes}
                      onChange={(e) => {
                        const updatedItems = [...form.values.agendaItems];
                        updatedItems[index] = { ...updatedItems[index], notes: e.currentTarget.value };
                        form.setFieldValue('agendaItems', updatedItems);
                      }}
                      error={form.errors[`agendaItems.${index}.notes` as keyof typeof form.errors]}
                    />
                    {loadingTenders ? <Loader size="sm" /> : (
                      <Select
                        label="Linked Tender (Optional)"
                        placeholder="Select a tender for this agenda item"
                        data={eligibleTenders}
                        value={item.tenderId}
                        onChange={(value) => {
                          const updatedItems = [...form.values.agendaItems];
                          updatedItems[index] = { ...updatedItems[index], tenderId: value };
                          form.setFieldValue('agendaItems', updatedItems);
                        }}
                        searchable
                        clearable
                      />
                    )}
                    {loadingSingleSource ? <Loader size="sm" /> : (
                      <Select
                        label="Linked Single Source Form (Optional)"
                        placeholder="Select a single source form for this agenda item"
                        data={eligibleSingleSourceForms}
                        value={item.singleSourceId}
                        onChange={(value) => {
                          const updatedItems = [...form.values.agendaItems];
                          updatedItems[index] = { ...updatedItems[index], singleSourceId: value };
                          form.setFieldValue('agendaItems', updatedItems);
                        }}
                        searchable
                        clearable
                      />
                    )}
                    {loadingPss ? <Loader size="sm" /> : (
                      <Select
                        label="Linked Project Summary Sheet PR-03 (Optional)"
                        placeholder="Approved PR-03 not yet on an agenda"
                        data={eligiblePss}
                        value={item.projectSummarySheetId}
                        onChange={(value) => {
                          const updatedItems = [...form.values.agendaItems];
                          updatedItems[index] = { ...updatedItems[index], projectSummarySheetId: value };
                          form.setFieldValue("agendaItems", updatedItems);
                        }}
                        searchable
                        clearable
                      />
                    )}
                    <AgendaTenderDocumentField
                      value={item.tenderDocument}
                      onChange={(doc) => {
                        const updatedItems = [...form.values.agendaItems];
                        updatedItems[index] = { ...updatedItems[index], tenderDocument: doc };
                        form.setFieldValue("agendaItems", updatedItems);
                      }}
                      onUploadingChange={(uploading) =>
                        setUploadingDocuments((count) => count + (uploading ? 1 : -1))
                      }
                    />
                  </Stack>
                </Card>
              ))}
              <Button
                leftSection={<IconPlus size={16} />}
                variant="light"
                onClick={addAgendaItem}
              >
                Add Agenda Item
              </Button>
            </Stack>

            <Group justify="space-between" mt="xl">
              <Button
                variant="outline"
                onClick={() => navigate("/bid-committee/meetings")}
              >
                Cancel
              </Button>
              <Button type="submit" loading={createMeeting.isPending} disabled={uploadingDocuments > 0}>
                Create Meeting
              </Button>
            </Group>
          </Stack>
        </form>
      </Card>
    </Stack>
  );
};
